<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Payroll Dashboard</h5>
          <p class="page-sub">Overview of payroll activity and trends</p>
        </div>
        <div class="page-actions">
  
          <!-- Country -->
          <select v-model="filters.country" class="filter-select">
            <option value="GB">🇬🇧 United Kingdom</option>
            <option value="NG">🇳🇬 Nigeria</option>
          </select>
  
          <!-- Date Range -->
          <div class="date-range">
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <input type="date" class="date-input" v-model="filters.from" />
            </div>
            <span class="date-sep">—</span>
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <input type="date" class="date-input" v-model="filters.to" />
            </div>
          </div>
  
          <button class="btn-primary-action" @click="load" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="search" class="me-1" />
            {{ loading ? 'Loading...' : 'Apply' }}
          </button>
  
        </div>
      </div>
  
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="cards-grid mb-4">
          <div class="skeleton skeleton--card" v-for="n in 5" :key="n"></div>
        </div>
        <div class="charts-grid mb-4">
          <div class="skeleton skeleton--chart"></div>
          <div class="skeleton skeleton--chart"></div>
        </div>
        <div class="skeleton skeleton--insight"></div>
      </template>
  
      <template v-else>
  
        <!-- Summary Cards -->
        <div class="cards-grid mb-4">
  
          <div class="stat-card stat-card--blue">
            <div class="stat-card-icon stat-card-icon--blue">
              <font-awesome-icon icon="money-bill-wave" />
            </div>
            <div>
              <p class="stat-label">Gross Pay</p>
              <p class="stat-value">{{ currency }}{{ formatPrice(cards.gross) }}</p>
            </div>
          </div>
  
          <div class="stat-card stat-card--green">
            <div class="stat-card-icon stat-card-icon--green">
              <font-awesome-icon icon="wallet" />
            </div>
            <div>
              <p class="stat-label">Net Pay</p>
              <p class="stat-value">{{ currency }}{{ formatPrice(cards.net) }}</p>
            </div>
          </div>
  
          <div class="stat-card stat-card--orange">
            <div class="stat-card-icon stat-card-icon--orange">
              <font-awesome-icon icon="landmark" />
            </div>
            <div>
              <p class="stat-label">PAYE Tax</p>
              <p class="stat-value">{{ currency }}{{ formatPrice(cards.paye) }}</p>
            </div>
          </div>
  
          <div class="stat-card stat-card--purple">
            <div class="stat-card-icon stat-card-icon--purple">
              <font-awesome-icon icon="shield-alt" />
            </div>
            <div>
              <p class="stat-label">National Insurance</p>
              <p class="stat-value">{{ currency }}{{ formatPrice(cards.ni) }}</p>
            </div>
          </div>
  
          <div class="stat-card stat-card--teal">
            <div class="stat-card-icon stat-card-icon--teal">
              <font-awesome-icon icon="piggy-bank" />
            </div>
            <div>
              <p class="stat-label">Pension</p>
              <p class="stat-value">{{ currency }}{{ formatPrice(cards.pension) }}</p>
            </div>
          </div>
  
        </div>
  
        <!-- Charts -->
        <div class="charts-grid mb-4">
  
          <div class="chart-card">
            <div class="chart-card-header">
              <div class="chart-card-icon chart-card-icon--blue">
                <font-awesome-icon icon="chart-line" />
              </div>
              <div>
                <p class="chart-card-title">Payroll Trends</p>
                <p class="chart-card-sub">Monthly gross vs net over time</p>
              </div>
            </div>
            <div class="chart-body">
              <PayrollLineChart :trends="trends" />
            </div>
          </div>
  
          <div class="chart-card">
            <div class="chart-card-header">
              <div class="chart-card-icon chart-card-icon--purple">
                <font-awesome-icon icon="chart-pie" />
              </div>
              <div>
                <p class="chart-card-title">Pay Breakdown</p>
                <p class="chart-card-sub">Distribution of payroll components</p>
              </div>
            </div>
            <div class="chart-body">
              <PayrollPieChart :breakdown="breakdown" />
            </div>
          </div>
  
        </div>
  
        <!-- Insights -->
        <div class="chart-card">
          <div class="chart-card-header">
            <div class="chart-card-icon chart-card-icon--green">
              <font-awesome-icon icon="lightbulb" />
            </div>
            <div>
              <p class="chart-card-title">Payroll Growth Insight</p>
              <p class="chart-card-sub">Period-over-period analysis</p>
            </div>
          </div>
          <div class="chart-body">
            <PayrollInsightsChart :insights="insights" />
          </div>
          <div v-if="insights.message" class="insight-message">
            <font-awesome-icon icon="info-circle" class="me-2" />
            {{ insights.message }}
          </div>
        </div>
  
      </template>
  
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue';
  import axios from 'axios';
  import Header               from './component/header.vue';
//   import DashboardCard        from './component/DashboardCard.vue';
  import PayrollLineChart     from './component/charts/PayrollLineChart.vue';
  import PayrollPieChart      from './component/charts/PayrollPieChart.vue';
  import PayrollInsightsChart from './component/charts/PayrollInsightsChart.vue';
  
  const filters   = ref({ country: 'GB', from: '', to: '' });
  const cards     = ref({});
  const trends    = ref([]);
  const breakdown = ref({});
  const insights  = ref({});
  const loading   = ref(false);
  
  const currency = computed(() => filters.value.country === 'GB' ? '£' : '₦');
  
  const formatPrice = (value) => {
    if (!value && value !== 0) return '0.00';
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const load = async () => {
    loading.value = true;
    try {
      const [c, t, b, i] = await Promise.all([
        axios.get('/dashboard/payroll/cards',     { params: filters.value }),
        axios.get('/dashboard/payroll/trends',    { params: filters.value }),
        axios.get('/dashboard/payroll/breakdown', { params: filters.value }),
        axios.get('/dashboard/payroll/insights'),
      ]);
      cards.value     = c.data;
      trends.value    = t.data;
      breakdown.value = b.data;
      insights.value  = i.data;
    } finally {
      loading.value = false;
    }
  };
  
  onMounted(load);
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; height:38px; }
  
  /* ── Date Range ── */
  .date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; height:38px; }
  .date-field { position:relative; display:flex; align-items:center; }
  .date-icon  { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
  .date-input { border:none; outline:none; font-size:13px; color:#1a2b45; padding-left:24px; background:transparent; width:130px; }
  .date-sep   { color:#aab8cc; font-size:13px; }
  
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; height:38px; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .me-1 { margin-right:4px; } .me-2 { margin-right:8px; }
  .mb-4 { margin-bottom:20px; }
  
  /* ── Skeleton ── */
  .skeleton     { border-radius:12px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--card    { height:100px; }
  .skeleton--chart   { height:320px; }
  .skeleton--insight { height:240px; }
  
  /* ── Cards Grid ── */
  .cards-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:16px; }
  
  .stat-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; transition:box-shadow .15s; }
  .stat-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
  
  .stat-card-icon { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .stat-card-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
  .stat-card-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
  .stat-card-icon--orange { background:rgba(224,140,0,.1);  color:#e08c00; }
  .stat-card-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
  .stat-card-icon--teal   { background:rgba(13,148,136,.1); color:#0d9488; }
  
  .stat-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin:0 0 4px; }
  .stat-value { font-size:20px; font-weight:800; color:#1a2b45; margin:0; }
  
  /* ── Charts Grid ── */
  .charts-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
  
  .chart-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .chart-card-header { display:flex; align-items:center; gap:14px; padding:18px 20px; border-bottom:1px solid #f0f4f8; }
  .chart-card-icon   { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .chart-card-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
  .chart-card-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
  .chart-card-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
  .chart-card-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .chart-card-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  .chart-body { padding:20px; }
  
  .insight-message { display:flex; align-items:center; padding:14px 20px; background:#f8fafc; border-top:1px solid #f0f4f8; font-size:13px; color:#4a5e78; }
  
  /* ── Responsive ── */
  @media(max-width:1200px) { .cards-grid { grid-template-columns:repeat(3,1fr); } }
  @media(max-width:991px)  { .cards-grid { grid-template-columns:repeat(2,1fr); } .charts-grid { grid-template-columns:1fr; } }
  @media(max-width:767px)  { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .cards-grid { grid-template-columns:1fr 1fr; } .date-range { flex-wrap:wrap; height:auto; } }
  @media(max-width:480px)  { .cards-grid { grid-template-columns:1fr; } }
  </style>